import { loadJwt } from '../auth/mobileMpcService';
import { baseUrl, apiVersion, encodeToBase58 } from '../../utils/dwclient/dwcommonutils';
import { splitPath } from '../../utils/paths';
import { attemptAutoRefreshResult, captureRefreshBinding, captureAuthInvalidationBinding, invalidateMobileAuth, isRefreshBindingCurrent, } from '../auth/authRefreshManager';
import { getKeySignature as getMobileKeySignature } from '../auth/mobileEncryption';
import { getKeySignature as getDesktopKeySignature, createFromSignature, encryptData, decryptData, exportCryptoKeyAsHex, } from '../../utils/dataEncryption';
const API_VERSION = apiVersion;
const NO_CACHE_HEADERS = {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
};
async function handleUnauthorizedWithRefresh(originalUrl, originalHeaders, requestBinding, originalMethod = 'GET') {
    if (!requestBinding || !isRefreshBindingCurrent(requestBinding))
        return null;
    console.log('[StorageAPI] 401 Unauthorized — attempting auto-refresh...');
    const refresh = await attemptAutoRefreshResult();
    const newJwt = refresh.status === 'success' ? refresh.jwt : null;
    if (newJwt) {
        // A successful refresh may rotate the token and invalidate its old binding.
        // Bind the retry to the committed credentials, not the pre-refresh session.
        const retryBinding = captureRefreshBinding(newJwt);
        if (!retryBinding)
            return null;
        console.log('[StorageAPI] Auto-refresh succeeded, retrying request');
        try {
            const retryResponse = await fetch(originalUrl, {
                method: originalMethod,
                headers: { ...originalHeaders, 'Authorization': `Bearer ${newJwt}`, ...NO_CACHE_HEADERS },
                cache: 'no-store',
            });
            if (retryResponse.ok) {
                // Wallet/session changes (or a newer JWT) while fetch was pending must
                // not let this response restore obsolete auth state in the provider.
                if (!isRefreshBindingCurrent(retryBinding) || loadJwt() !== newJwt)
                    return null;
                window.dispatchEvent(new CustomEvent('mobile-auth-refreshed', { detail: { jwt: newJwt } }));
                return retryResponse;
            }
            console.warn('[StorageAPI] Retry after refresh still failed:', retryResponse.status);
        }
        catch (e) {
            console.error('[StorageAPI] Retry fetch error:', e);
        }
    }
    if (refresh.status === 'rejected' && isRefreshBindingCurrent(refresh.binding)) {
        console.warn('[StorageAPI] Refresh was revoked — clearing auth data');
        invalidateMobileAuth(captureAuthInvalidationBinding(), 'refresh_rejected');
    }
    else {
        console.warn('[StorageAPI] Refresh temporarily unavailable or missing; preserving auth data');
    }
    return null;
}
async function getEncryptionKeyHex() {
    try {
        const cryptoKey = await getMobileSymmetricKey();
        return exportCryptoKeyAsHex(cryptoKey);
    }
    catch {
        return null;
    }
}
export async function fetchFilesListWithJwt(path = '/', recurse = false) {
    const token = loadJwt();
    const requestBinding = captureRefreshBinding(token);
    if (!token) {
        return {
            data: null,
            error: 'Not authenticated',
            status: 401,
        };
    }
    const sendObject = {
        path: path,
        recurse: recurse,
    };
    const message = encodeToBase58(JSON.stringify(sendObject));
    const endpoint = `/api/${API_VERSION}/files/list`;
    const url = `${baseUrl}${endpoint}?message=${message}`;
    try {
        console.log('[StorageAPI] Fetching files:', url.substring(0, 80) + '...', 'JWT length:', token.length);
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                ...NO_CACHE_HEADERS,
            },
            cache: 'no-store',
        });
        if (!response.ok) {
            if (response.status === 401) {
                const retryResponse = await handleUnauthorizedWithRefresh(url, { 'Authorization': `Bearer ${token}` }, requestBinding);
                if (retryResponse) {
                    const rawData = await retryResponse.json();
                    let data;
                    if (Array.isArray(rawData)) {
                        data = { entries: rawData };
                    }
                    else if (rawData.entries) {
                        data = rawData;
                    }
                    else {
                        data = { entries: [] };
                    }
                    return { data, error: null, status: retryResponse.status };
                }
            }
            let errorMessage = `HTTP ${response.status}`;
            try {
                const errorData = await response.json();
                errorMessage = errorData.message || errorData.error || errorMessage;
            }
            catch {
                errorMessage = response.statusText || errorMessage;
            }
            return {
                data: null,
                error: errorMessage,
                status: response.status,
            };
        }
        const rawData = await response.json();
        console.log('[StorageAPI] Raw response:', JSON.stringify(rawData).slice(0, 500));
        let data;
        if (Array.isArray(rawData)) {
            data = { entries: rawData };
        }
        else if (rawData.entries) {
            data = rawData;
        }
        else {
            data = { entries: [] };
        }
        return {
            data,
            error: null,
            status: response.status,
        };
    }
    catch (error) {
        if (error instanceof Error) {
            return {
                data: null,
                error: error.message,
                status: 0,
            };
        }
        return {
            data: null,
            error: 'Unknown error occurred',
            status: 0,
        };
    }
}
export function getFileExtension(path) {
    const parsed = splitPath(path);
    if (!parsed || !parsed.extension)
        return '';
    return parsed.extension.replace(/^\./, '').toLowerCase();
}
export function getFileName(path) {
    const parsed = splitPath(path);
    return parsed?.name || path;
}
export function getParentPath(path) {
    const parsed = splitPath(path);
    return parsed?.folder || '/';
}
export function formatFileSize(bytes) {
    if (bytes === 0)
        return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}
export function formatDate(timestamp) {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).format(date);
}
async function getMobileSymmetricKey() {
    const signature = getDesktopKeySignature() || getMobileKeySignature();
    if (!signature) {
        throw new Error('No encryption key available');
    }
    return createFromSignature(signature);
}
async function decryptFileBlob(encryptedBlob) {
    const key = await getMobileSymmetricKey();
    const encryptedData = new Uint8Array(await encryptedBlob.arrayBuffer());
    const decodedBytes = await decryptData(key, encryptedData);
    return new Blob([decodedBytes], { type: encryptedBlob.type || 'application/octet-stream' });
}
async function encryptFileBlob(plainBlob) {
    const key = await getMobileSymmetricKey();
    const plainData = new Uint8Array(await plainBlob.arrayBuffer());
    const encodedBytes = await encryptData(key, plainData);
    return new Blob([encodedBytes], { type: 'application/octet-stream' });
}
export async function saveMobileUserProfile(profile) {
    const token = await loadJwt();
    if (!token)
        return { data: null, error: 'Not authenticated', status: 401 };
    try {
        const jsonBlob = new Blob([JSON.stringify(profile)], { type: 'text/json' });
        const encryptedBlob = await encryptFileBlob(jsonBlob);
        const formData = new FormData();
        formData.append('file', new File([encryptedBlob], 'user_profile.json'));
        const resp = await fetch(`${baseUrl}/api/${API_VERSION}/users/profile`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData,
        });
        if (!resp.ok) {
            return { data: null, error: `Server error ${resp.status}`, status: resp.status };
        }
        return { data: null, error: null, status: 200 };
    }
    catch (err) {
        console.warn('[StorageAPI] saveMobileUserProfile error:', err);
        return { data: null, error: err.message, status: 500 };
    }
}
async function getFileContentChunked(token, path, onProgress) {
    const symmetricKey = await getSymmetricKeyForMobile();
    if (!symmetricKey) {
        return { data: null, error: 'Encryption key not available', status: 401 };
    }
    const sendObject = { path };
    const message = encodeToBase58(JSON.stringify(sendObject));
    const endpointUrl = `${baseUrl}/api/${API_VERSION}/files/read-part?message=${message}`;
    try {
        const decryptedParts = [];
        let chunkIndex = 0;
        let lastChunkSize = CHUNK_GROSS;
        while (lastChunkSize === CHUNK_GROSS) {
            const start = chunkIndex * CHUNK_GROSS;
            const end = start + CHUNK_GROSS - 1;
            const response = await fetch(endpointUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Range': `bytes=${start}-${end}`,
                    ...NO_CACHE_HEADERS,
                },
                cache: 'no-store',
            });
            if (!response.ok && response.status !== 206) {
                return { data: null, error: `Chunked read failed at chunk ${chunkIndex}: HTTP ${response.status}`, status: response.status };
            }
            const encryptedChunk = new Uint8Array(await response.arrayBuffer());
            lastChunkSize = encryptedChunk.byteLength;
            if (lastChunkSize === 0)
                break;
            const decryptedChunk = await decryptChunk(symmetricKey, encryptedChunk);
            decryptedParts.push(decryptedChunk);
            if (onProgress) {
                const totalDecrypted = decryptedParts.reduce((sum, p) => sum + p.byteLength, 0);
                onProgress(totalDecrypted);
            }
            chunkIndex++;
        }
        const blob = new Blob(decryptedParts, { type: 'application/octet-stream' });
        return { data: blob, error: null, status: 200 };
    }
    catch (error) {
        return { data: null, error: error instanceof Error ? error.message : 'Chunked read failed', status: 500 };
    }
}
export async function getFileContent(path) {
    const token = loadJwt();
    const requestBinding = captureRefreshBinding(token);
    if (!token) {
        return { data: null, error: 'Not authenticated', status: 401 };
    }
    const chunked = await checkIsChunked(token, path);
    if (chunked) {
        return getFileContentChunked(token, path);
    }
    const fileName = getFileName(path);
    // Strategy 1: Download encrypted file and decrypt client-side (like desktop)
    try {
        const readSendObject = { path };
        const readMessage = encodeToBase58(JSON.stringify(readSendObject));
        const readEndpoint = `/api/${API_VERSION}/files/read`;
        const readUrl = `${baseUrl}${readEndpoint}?message=${readMessage}`;
        console.log('[StorageAPI] Trying client-side decrypt approach:', { path, fileName });
        const readResponse = await fetch(readUrl, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}`, ...NO_CACHE_HEADERS },
            cache: 'no-store',
        });
        if (readResponse.ok) {
            const encryptedBlob = await readResponse.blob();
            console.log('[StorageAPI] Encrypted file downloaded:', { size: encryptedBlob.size });
            if (encryptedBlob.size > 0) {
                try {
                    const decryptedBlob = await decryptFileBlob(encryptedBlob);
                    console.log('[StorageAPI] File decrypted client-side successfully:', { decryptedSize: decryptedBlob.size });
                    return { data: decryptedBlob, error: null, status: 200 };
                }
                catch (decryptErr) {
                    console.warn('[StorageAPI] Client-side decryption failed, trying server-side:', decryptErr);
                }
            }
        }
        else if (readResponse.status === 401) {
            const retryResponse = await handleUnauthorizedWithRefresh(readUrl, { 'Authorization': `Bearer ${token}` }, requestBinding);
            if (!retryResponse) {
                // An auth outage/rejection is not a decryption failure. Do not start
                // another refresh through the server-side fallback with the old token.
                return { data: null, error: 'Authentication expired', status: 401 };
            }
            if (retryResponse && retryResponse.ok) {
                const encryptedBlob = await retryResponse.blob();
                if (encryptedBlob.size > 0) {
                    try {
                        const decryptedBlob = await decryptFileBlob(encryptedBlob);
                        return { data: decryptedBlob, error: null, status: 200 };
                    }
                    catch (decryptErr) {
                        console.warn('[StorageAPI] Client-side decryption failed after auth refresh:', decryptErr);
                    }
                }
            }
        }
    }
    catch (err) {
        console.warn('[StorageAPI] Client-side decrypt approach failed:', err);
    }
    // Strategy 2: Fall back to server-side decryption via files-mobile/read
    const userEncryptionKey = await getEncryptionKeyHex();
    if (!userEncryptionKey) {
        return { data: null, error: 'Encryption key not available. Please log in again.', status: 401 };
    }
    const sendObject = {
        path: path,
        fileName: fileName,
        userEncryptionKey: userEncryptionKey,
    };
    const message = encodeToBase58(JSON.stringify(sendObject));
    const endpoint = `/api/${API_VERSION}/files-mobile/read`;
    const downloadUrl = `${baseUrl}${endpoint}?message=${message}`;
    console.log('[StorageAPI] Trying server-side decrypt fallback:', { path, fileName });
    try {
        const response = await fetch(downloadUrl, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}`, ...NO_CACHE_HEADERS },
            cache: 'no-store',
        });
        if (!response.ok) {
            if (response.status === 401) {
                const retryResponse = await handleUnauthorizedWithRefresh(downloadUrl, { 'Authorization': `Bearer ${token}` }, requestBinding);
                if (retryResponse) {
                    const blob = await retryResponse.blob();
                    return { data: blob, error: null, status: retryResponse.status };
                }
            }
            let errorMessage = `HTTP ${response.status}`;
            try {
                const errorBody = await response.text();
                console.error('[StorageAPI] Server-side decrypt error:', errorBody.substring(0, 500));
                try {
                    const errorData = JSON.parse(errorBody);
                    errorMessage = errorData.message || errorData.error || errorMessage;
                }
                catch {
                    errorMessage = errorBody.substring(0, 200) || response.statusText || errorMessage;
                }
            }
            catch {
                errorMessage = response.statusText || errorMessage;
            }
            return { data: null, error: errorMessage, status: response.status };
        }
        const blob = await response.blob();
        return { data: blob, error: null, status: response.status };
    }
    catch (error) {
        return {
            data: null,
            error: error instanceof Error ? error.message : 'Failed to fetch file',
            status: 500,
        };
    }
}
export async function downloadFolderAsZip(folderPath, folderName, onProgress, knownFiles) {
    try {
        const JSZip = (await import('jszip')).default;
        let filesToDownload;
        if (knownFiles && knownFiles.length > 0) {
            filesToDownload = knownFiles.map(f => ({
                name: f.name,
                downloadPath: f.storagePath,
                relativePath: f.name,
            }));
        }
        else {
            const listResult = await fetchFilesListWithJwt(folderPath, true);
            if (listResult.error || !listResult.data) {
                return { success: false, error: listResult.error || 'FOLDER_LIST_FAILED' };
            }
            const apiFiles = listResult.data.entries.filter(e => e.isFile);
            filesToDownload = apiFiles.map(f => ({
                name: f.path.split('/').pop() || 'file',
                downloadPath: f.path,
                relativePath: f.path.startsWith(folderPath)
                    ? f.path.substring(folderPath.length).replace(/^\//, '')
                    : f.path.split('/').pop() || 'file',
            }));
        }
        if (filesToDownload.length === 0) {
            return { success: false, error: 'FOLDER_EMPTY' };
        }
        const zip = new JSZip();
        let downloaded = 0;
        for (const file of filesToDownload) {
            if (onProgress) {
                onProgress(downloaded, filesToDownload.length, file.relativePath);
            }
            const content = await getFileContent(file.downloadPath);
            if (content.data) {
                zip.file(file.relativePath, content.data);
            }
            downloaded++;
        }
        if (onProgress) {
            onProgress(filesToDownload.length, filesToDownload.length, '');
        }
        const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${folderName}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        return { success: true, error: null };
    }
    catch (error) {
        if (error instanceof Error) {
            return { success: false, error: error.message };
        }
        return { success: false, error: 'FOLDER_DOWNLOAD_FAILED' };
    }
}
export async function downloadFile(path, fileName) {
    const response = await getFileContent(path);
    if (response.error || !response.data) {
        return { success: false, error: response.error || 'Failed to download file' };
    }
    try {
        const url = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        return { success: true, error: null };
    }
    catch (error) {
        if (error instanceof Error) {
            return { success: false, error: error.message };
        }
        return { success: false, error: 'Failed to trigger download' };
    }
}
async function checkIsChunked(token, path) {
    try {
        const sendObject = { path };
        const message = encodeToBase58(JSON.stringify(sendObject));
        const url = `${baseUrl}/api/${API_VERSION}/files/metadata?message=${message}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}`, ...NO_CACHE_HEADERS },
            cache: 'no-store',
        });
        if (!response.ok)
            return false;
        const data = await response.json();
        return Boolean(data?.metadata?.UPLOADED_IN_CHUNKS);
    }
    catch {
        return false;
    }
}
async function getSymmetricKeyForMobile() {
    try {
        return await getMobileSymmetricKey();
    }
    catch {
        return null;
    }
}
async function decryptChunk(symmetricKey, encryptedData) {
    return decryptData(symmetricKey, encryptedData);
}
const CHUNK_NET = 67_108_836;
const CHUNK_OVERHEAD = 12 + 16;
const CHUNK_GROSS = CHUNK_NET + CHUNK_OVERHEAD;
async function downloadFileChunked(token, path, fileName, onProgress) {
    const result = await getFileContentChunked(token, path, onProgress);
    if (result.error || !result.data) {
        return { success: false, error: result.error || 'Chunked download failed' };
    }
    const url = URL.createObjectURL(result.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    return { success: true, error: null };
}
export async function downloadFileDirect(path, fileName, onProgress) {
    const token = await loadJwt();
    if (!token) {
        return { success: false, error: 'Not authenticated' };
    }
    const chunked = await checkIsChunked(token, path);
    if (chunked) {
        return downloadFileChunked(token, path, fileName, onProgress);
    }
    const userEncryptionKey = await getEncryptionKeyHex();
    if (!userEncryptionKey) {
        return { success: false, error: 'Encryption key not available. Please log in again.' };
    }
    const sendObject = {
        path: path,
        fileName: fileName,
        userEncryptionKey: userEncryptionKey,
    };
    const message = encodeToBase58(JSON.stringify(sendObject));
    const endpoint = `/api/${API_VERSION}/files-mobile/read`;
    const nativeDlUrl = `${baseUrl}${endpoint}?authToken=${encodeURIComponent(token)}&message=${message}`;
    try {
        const a = document.createElement('a');
        a.href = nativeDlUrl;
        a.download = fileName;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return { success: true, error: null };
    }
    catch (error) {
        return { success: false, error: error instanceof Error ? error.message : 'Download failed' };
    }
}
export async function getFileTextContent(path) {
    const response = await getFileContent(path);
    if (response.error || !response.data) {
        return {
            data: null,
            error: response.error || 'Failed to get file content',
            status: response.status,
        };
    }
    try {
        const text = await response.data.text();
        return {
            data: text,
            error: null,
            status: response.status,
        };
    }
    catch (error) {
        if (error instanceof Error) {
            return {
                data: null,
                error: error.message,
                status: 0,
            };
        }
        return {
            data: null,
            error: 'Failed to read file content',
            status: 0,
        };
    }
}
export async function deleteFileFromStorage(path) {
    const token = loadJwt();
    const requestBinding = captureRefreshBinding(token);
    if (!token) {
        return { success: false, error: 'Not authenticated' };
    }
    const sendObject = { path };
    const message = encodeToBase58(JSON.stringify(sendObject));
    try {
        const response = await fetch(`${baseUrl}/api/${API_VERSION}/files/remove?message=${message}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });
        if (!response.ok) {
            if (response.status === 401) {
                const retryResponse = await handleUnauthorizedWithRefresh(`${baseUrl}/api/${API_VERSION}/files/remove?message=${message}`, { 'Authorization': `Bearer ${token}` }, requestBinding, 'DELETE');
                if (retryResponse) {
                    return { success: true };
                }
                return { success: false, error: 'Authentication expired' };
            }
            const errorData = await response.json().catch(() => ({ error: response.statusText }));
            return { success: false, error: errorData.error || `Delete failed (${response.status})` };
        }
        return { success: true };
    }
    catch (error) {
        return { success: false, error: error instanceof Error ? error.message : 'Delete request failed' };
    }
}
export function isPreviewableType(fileType) {
    return ['image', 'video', 'audio', 'text', 'code', 'document'].includes(fileType);
}
export function getFileType(entry) {
    if (!entry.isFile) {
        return 'folder';
    }
    const ext = getFileExtension(entry.path);
    const contentType = entry.metadata?.['Content-Type'] || '';
    if (contentType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)) {
        return 'image';
    }
    if (contentType.startsWith('video/') || ['mp4', 'webm', 'avi', 'mov', 'mkv', 'flv'].includes(ext)) {
        return 'video';
    }
    if (contentType.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a'].includes(ext)) {
        return 'audio';
    }
    if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods'].includes(ext)) {
        return 'document';
    }
    if (['txt', 'md', 'rtf', 'csv'].includes(ext)) {
        return 'text';
    }
    if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2'].includes(ext)) {
        return 'archive';
    }
    if (['js', 'ts', 'jsx', 'tsx', 'json', 'html', 'css', 'py', 'java', 'cpp', 'c', 'go', 'rs'].includes(ext)) {
        return 'code';
    }
    return 'file';
}
export async function getMobileUserProfile() {
    const token = await loadJwt();
    if (!token)
        return { data: null, error: 'Not authenticated', status: 401 };
    try {
        const resp = await fetch(`${baseUrl}/api/${API_VERSION}/users/profile`, {
            method: 'GET',
            headers: { ...NO_CACHE_HEADERS, 'Authorization': `Bearer ${token}` },
        });
        if (!resp.ok) {
            return { data: { name: '', email: '' }, error: null, status: resp.status };
        }
        const blob = await resp.blob();
        if (blob.size === 0) {
            return { data: { name: '', email: '' }, error: null, status: 200 };
        }
        const decrypted = await decryptFileBlob(blob);
        const text = await new Response(decrypted).text();
        return { data: JSON.parse(text), error: null, status: 200 };
    }
    catch (err) {
        console.warn('[StorageAPI] getMobileUserProfile error:', err);
        return { data: { name: '', email: '' }, error: err.message, status: 500 };
    }
}
export async function getMobileAvatarBase64() {
    const token = await loadJwt();
    if (!token)
        return null;
    try {
        const resp = await fetch(`${baseUrl}/api/${API_VERSION}/users/avatar`, {
            method: 'GET',
            headers: { ...NO_CACHE_HEADERS, 'Authorization': `Bearer ${token}` },
        });
        if (!resp.ok)
            return null;
        const blob = await resp.blob();
        if (blob.size === 0)
            return null;
        const decrypted = await decryptFileBlob(blob);
        return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(new Blob([decrypted], { type: 'image/jpeg' }));
        });
    }
    catch (err) {
        console.warn('[StorageAPI] getMobileAvatarBase64 error:', err);
        return null;
    }
}
